import Glide from "@glidejs/glide";
import useNcId from "hooks/useNcId";
import { FC, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import NextPrev from "shared/NextPrev/NextPrev";
import { getProjects } from "data/api";
import SafeImage from "components/SafeImage/SafeImage";

const SectionProjectsPreview: FC = () => {
  const [projects, setProjects] = useState<any[]>([]);
  const UNIQUE_CLASS = "ProjectsPreview" + useNcId();

  useEffect(() => {
    getProjects().then((items) => {
      setProjects(
        items.slice(0, 6).map((item: any) => ({
          id: item.id,
          name: item.name,
          image: item.images,
          href: `/projects/${item.id}`,
        }))
      );
    });
  }, []);

  const glide = useMemo(() => {
    return new Glide(`.${UNIQUE_CLASS}`, {
      type: "carousel",
      perView: 3,
      gap: 20,
      bound: true,
      breakpoints: {
        1024: { perView: 3, gap: 16 },
        768: { perView: 2, gap: 16 },
        480: { perView: 1.2, gap: 12 },
      },
    });
  }, [UNIQUE_CLASS]);

  useEffect(() => {
    if (projects.length === 0) return;
    const timer = setTimeout(() => glide.mount(), 80);
    return () => {
      clearTimeout(timer);
      try { glide.destroy(); } catch (_) {}
    };
  }, [glide, projects.length]);

  if (projects.length === 0) return null;

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-neutral-50 dark:bg-neutral-800/50">
      <div className="container px-4 sm:px-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-center text-neutral-900 dark:text-white mb-8 sm:mb-10">
          Projects
        </h2>

        <div className={`${UNIQUE_CLASS} relative group`}>
          <div className="glide__track" data-glide-el="track">
            <ul className="glide__slides">
              {projects.map((p) => (
                <li key={p.id} className="glide__slide">
                  <Link to={p.href} className="block group/card">
                    <div className="bg-white dark:bg-neutral-800 rounded-2xl overflow-hidden shadow-bcr-card hover:shadow-bcr-card-hover transition-all duration-300 hover:-translate-y-1">
                      <div className="aspect-[16/10] max-h-[160px] overflow-hidden bg-neutral-100 dark:bg-neutral-800">
                        <SafeImage
                          src={p.image}
                          alt={p.name}
                          placeholderVariant="project"
                          placeholderCompact
                          objectFit="cover"
                          imgClassName="group-hover/card:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4 text-center">
                        <h3 className="font-semibold text-sm sm:text-base text-neutral-900 dark:text-white line-clamp-1">
                          {p.name}
                        </h3>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="hidden sm:flex absolute opacity-0 group-hover:opacity-100 transition-opacity top-1/2 -translate-y-1/2 inset-x-0 justify-between pointer-events-none">
            <NextPrev
              className="w-full justify-between pointer-events-auto"
              btnClassName="w-9 h-9 shadow-bcr bg-white dark:bg-neutral-800"
            />
          </div>
        </div>

        <div className="text-center mt-6">
          <Link to="/projects" className="text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors">
            View All Projects &rarr;
          </Link>
        </div>
      </div>
    </section>
  );
};

export default SectionProjectsPreview;
